import { DuplicateIcon } from '@heroicons/react/outline'
import { Fragment, useEffect, useRef, useState } from 'react'
import HttpService from '../services/HttpService'
import { Button, Card, EmptyState, Field, KebabMenu, Modal, Page, Select, inputClass } from './ui'

const extractApiKey = (data) =>
    data?.apiKey || data?.api_key || data?.key || data?.canal?.apiKey || ''

export default function Channels(props) {
    const [canales, setCanales] = useState([])
    const [open, setOpen] = useState(false)
    const [id, setId] = useState('')
    const [nombreCanal, setNombreCanal] = useState('')
    const [cuentas, setCuentas] = useState([])
    const [cuentaSelected, setCuentaSelected] = useState({})
    const [validateNombreCanalError, setValidateNombreCanalError] = useState()
    const [validateCuentaError, setValidateCuentaError] = useState()
    const [modalTitle, setModalTitle] = useState()
    const [revealedApiKey, setRevealedApiKey] = useState('')
    const [copied, setCopied] = useState(false)
    const validForm = useRef(true)
    const creating = useRef(true)

    useEffect(() => {
        props.handleTitulo('Canales de cobro')
        getCanales()
        getCatalogos()
    }, [])

    const getCanales = () => {
        HttpService.getAxiosClient().get('/canales').then((res) => setCanales(res.data))
    }

    const getCatalogos = () => {
        HttpService.getAxiosClient().get('/catalogos/canales').then((res) => setCuentas(res.data.cuentas))
    }

    const handleNombreCanal = (nombreCanal) => {
        setNombreCanal(nombreCanal)
        setValidateNombreCanalError(null)
    }

    const handleCuentaSelected = (cuenta) => {
        setCuentaSelected(cuenta)
        setValidateCuentaError(null)
    }

    const showApiKey = (data) => {
        const apiKey = extractApiKey(data)
        setCopied(false)
        setRevealedApiKey(apiKey)
        setModalTitle('API Key del canal')
        setOpen(true)
    }

    const copyApiKey = async () => {
        if (!revealedApiKey) return
        try {
            await navigator.clipboard.writeText(revealedApiKey)
            setCopied(true)
        } catch {
            const input = document.createElement('textarea')
            input.value = revealedApiKey
            document.body.appendChild(input)
            input.select()
            document.execCommand('copy')
            document.body.removeChild(input)
            setCopied(true)
        }
    }

    const closeModal = () => {
        setOpen(false)
        setRevealedApiKey('')
        setCopied(false)
    }

    const saveChannel = () => {
        if (revealedApiKey) return
        validateForm()
        if (validForm.current) {
            if (creating.current) {
                HttpService.getAxiosClient()
                    .post('/canales', { nombreCanal: nombreCanal, idCuenta: cuentaSelected.id })
                    .then((res) => {
                        getCanales()
                        showApiKey(res.data)
                    })
            } else {
                HttpService.getAxiosClient()
                    .post(`/canales/${id}`, { nombreCanal: nombreCanal, idCuenta: cuentaSelected.id })
                    .then(() => {
                        getCanales()
                        closeModal()
                    })
            }
        }
    }

    const validateForm = () => {
        validForm.current = true
        if (!nombreCanal.trim()) {
            setValidateNombreCanalError('Debe especificar un nombre para el canal')
            validForm.current = false
        }
        if (!cuentaSelected.id) {
            setValidateCuentaError('Debe seleccionar una cuenta')
        }
    }

    const editar = (canal) => {
        creating.current = false
        setRevealedApiKey('')
        setCopied(false)
        setOpen(true)
        setModalTitle('Editar canal')
        setNombreCanal(canal.nombreCanal)
        setCuentaSelected(cuentas.find((x) => x.id === canal.cuenta) || {})
        setId(canal.id)
    }

    const rotateApiKey = (canal) => {
        HttpService.getAxiosClient().post(`/canales/${canal.id}/rotate-key`).then((res) => {
            getCanales()
            showApiKey(res.data)
        })
    }

    return (
        <Page
            description="Credenciales y configuración para operar CoDi desde tus integraciones."
            actions={
                <Button
                    onClick={() => {
                        creating.current = true
                        setRevealedApiKey('')
                        setCopied(false)
                        setOpen(true)
                        setModalTitle('Agregar canal de cobro')
                        setNombreCanal('')
                        setCuentaSelected({})
                    }}
                >
                    Nuevo canal
                </Button>
            }
        >
            {canales.length === 0 ? (
                <EmptyState title="Aún no hay canales" description="Crea un canal para comenzar a cobrar." />
            ) : (
                <ul className="grid gap-4 lg:grid-cols-2">
                    {canales.map((canal) => (
                        <li key={canal.id}>
                            <Card>
                                <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
                                    <div>
                                        <p className="text-sm font-semibold uppercase tracking-wide text-slate-900">{canal.nombreCanal}</p>
                                        <p className="mt-0.5 font-mono text-xs text-slate-400">{canal.id}</p>
                                    </div>
                                    <KebabMenu
                                        items={[
                                            { label: 'Editar', onClick: () => editar(canal) },
                                            { label: 'Rotar API Key', onClick: () => rotateApiKey(canal) },
                                            { label: 'Eliminar', onClick: () => {}, danger: true },
                                        ]}
                                    />
                                </div>
                                <div className="space-y-2 px-5 py-4 text-sm">
                                    <p><span className="text-slate-500">Cuenta</span> <span className="ml-2 text-slate-900">{canal.nombreCuenta}</span></p>
                                    <p><span className="text-slate-500">API Key Prefix</span> <span className="ml-2 font-mono text-xs text-slate-700">{canal.apiKeyPrefix ?? '—'}</span></p>
                                </div>
                            </Card>
                        </li>
                    ))}
                </ul>
            )}

            <Modal
                open={open}
                onClose={closeModal}
                title={modalTitle}
                footer={
                    revealedApiKey ? (
                        <Button onClick={closeModal}>Listo, ya la copié</Button>
                    ) : (
                        <Fragment>
                            <Button variant="secondary" onClick={closeModal}>Cancelar</Button>
                            <Button onClick={saveChannel}>Guardar</Button>
                        </Fragment>
                    )
                }
            >
                {revealedApiKey ? (
                    <>
                        <p className="text-sm text-amber-800">
                            Esta es la única vez que podrás ver la API Key completa. Cópiala y guárdala en un lugar seguro. Después solo verás el prefijo.
                        </p>
                        <div className="flex items-stretch gap-2">
                            <code className="block flex-1 break-all rounded-xl bg-slate-50 px-3 py-2 font-mono text-sm text-slate-900 ring-1 ring-inset ring-slate-200">
                                {revealedApiKey}
                            </code>
                            <Button variant="secondary" onClick={copyApiKey} className="shrink-0">
                                <DuplicateIcon className="mr-1.5 h-4 w-4" aria-hidden="true" />
                                {copied ? 'Copiada' : 'Copiar'}
                            </Button>
                        </div>
                    </>
                ) : (
                    <>
                        <Field label="Nombre del canal" error={validateNombreCanalError}>
                            <input type="text" value={nombreCanal} onChange={(e) => handleNombreCanal(e.target.value)} className={inputClass} />
                        </Field>
                        <Field label="Cuenta" error={validateCuentaError}>
                            <Select value={cuentaSelected} onChange={handleCuentaSelected} options={cuentas} />
                        </Field>
                    </>
                )}
            </Modal>
        </Page>
    )
}
